import { ExternalLink, Lock, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import {
  useAdminResources,
  useCreateResource,
  useRemoveResource,
  useReviewResource,
  useResourceFile,
  useUpdateResource,
  type AdminResource,
  type ResourceInput,
} from "@/features/resources/resources";
import { Pager } from "@/components/Pager";
import { RowListSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { SortableHeader, type SortState } from "@/components/SortableHeader";

const EMPTY: ResourceInput = {
  title: "",
  sort_as: "",
  description: "",
  url: "",
  category: "",
  tags: [],
  location: "",
  email: "",
  phone: "",
  access: "all",
};

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  published: "default",
  pending: "secondary",
  declined: "outline",
};

function ResourceDialog({
  resource,
  open,
  onOpenChange,
}: {
  resource: AdminResource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateResource();
  const update = useUpdateResource();
  const file = useResourceFile();
  const [form, setForm] = useState<ResourceInput>(
    resource
      ? {
          title: resource.title,
          sort_as: resource.sort_as ?? "",
          description: resource.description,
          // The link as typed. `url` is the resolved one, which is our own
          // copy when a file is attached, and saving that would overwrite the
          // publisher's address with ours.
          url: resource.link ?? resource.url,
          category: resource.category,
          tags: resource.tags,
          location: resource.location ?? "",
          email: resource.email ?? "",
          phone: resource.phone ?? "",
          access: resource.access,
        }
      : EMPTY
  );
  // The box holds what was typed, and the list is parsed alongside it.
  // Rendering `tags.join(", ")` back into the input swallowed the comma as it
  // was typed, so "Article, Legal" became one tag called "ArticleLegal".
  const [tagText, setTagText] = useState((resource?.tags ?? []).join(", "));
  const mutation = resource ? update : create;
  const close = () => onOpenChange(false);

  // A new entry has no id to hang a file off yet, so the chosen file waits
  // here and goes up the moment the entry exists (Sami, 2026-09-29: the box
  // was only offered when editing).
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  function submit() {
    if (resource) update.mutate({ id: resource.id, ...form }, { onSuccess: close });
    else
      create.mutate(form, {
        onSuccess: ({ id }) => {
          if (pendingFile) file.mutate({ id, file: pendingFile }, { onSuccess: close });
          else close();
        },
      });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{resource ? "Edit resource" : "Add resource"}</DialogTitle>
          <DialogDescription>
            Published resources appear in the member library straight away.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(["title", "url", "category"] as const).map((key) => (
            <div key={key} className="space-y-2">
              <Label htmlFor={`res-${key}`} className="capitalize">
                {key}
              </Label>
              <Input
                id={`res-${key}`}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
            </div>
          ))}
          <div className="space-y-2">
            <Label htmlFor="res-description">Description</Label>
            {/* The box grows to fit what it holds, and some descriptions run
                to a long paragraph: capped, it scrolls itself and keeps Save
                on screen. */}
            <Textarea
              id="res-description"
              rows={3}
              className="max-h-48"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          {/* Where it files in A to Z, for the entries the automatic order
              gets wrong. Blank files a person under their surname and
              anything else under its title, which is right for nearly all of
              them, so the box says so rather than asking. */}
          <div className="space-y-2">
            <Label htmlFor="res-sort-as">Sort as</Label>
            <Input
              id="res-sort-as"
              placeholder="Automatic: a person by surname, anything else by title"
              value={form.sort_as ?? ""}
              onChange={(e) => setForm({ ...form, sort_as: e.target.value })}
            />
          </div>
          {/* Speciality only: where it is and how to reach it are their own
              fields, because the imported library mixed all three into one tag
              list and they behave nothing alike. */}
          <div className="space-y-2">
            <Label htmlFor="res-tags">Tags</Label>
            <Input
              id="res-tags"
              placeholder="Article, Legal"
              value={tagText}
              onChange={(e) => {
                setTagText(e.target.value);
                setForm({
                  ...form,
                  tags: e.target.value
                    .split(",")
                    .map((t) => t.trim())
                    .filter(Boolean),
                });
              }}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="res-location">Location</Label>
              <Input
                id="res-location"
                value={form.location ?? ""}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="res-email">Email</Label>
              <Input
                id="res-email"
                value={form.email ?? ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="res-phone">Phone</Label>
              <Input
                id="res-phone"
                value={form.phone ?? ""}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
          </div>
          {/* Our own copy of a document, for the article that is a PDF on
              somebody else's site. A member following the entry gets this
              rather than the link. On a new entry it is held until Create. */}
          <div className="space-y-2">
            <Label htmlFor="res-file">File</Label>
            {!resource ? (
              <Input
                id="res-file"
                type="file"
                disabled={mutation.isPending || file.isPending}
                onChange={(e) => setPendingFile(e.target.files?.[0] ?? null)}
              />
            ) : resource.file_url ? (
              <div className="flex items-center gap-2">
                <a
                  href={resource.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm underline underline-offset-4"
                >
                  Uploaded file
                </a>
                <Button
                  size="xs"
                  variant="ghost"
                  disabled={file.isPending}
                  onClick={() => file.mutate({ id: resource.id, file: null })}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <Input
                id="res-file"
                type="file"
                disabled={file.isPending}
                onChange={(e) => {
                  const chosen = e.target.files?.[0];
                  if (chosen) file.mutate({ id: resource.id, file: chosen });
                }}
              />
            )}
            {file.error && (
              <p className="text-destructive text-sm">{file.error.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Access</Label>
            <Select
              value={form.access}
              onValueChange={(access) =>
                setForm({ ...form, access: access as "all" | "paid" })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All members, including free</SelectItem>
                <SelectItem value="paid">
                  Full members only (paid and gifted)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mutation.error && (
            <p className="text-destructive text-sm">{mutation.error.message}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              !form.title ||
              !(form.url || pendingFile || resource?.file_url) ||
              mutation.isPending ||
              file.isPending
            }
          >
            {(mutation.isPending || file.isPending) && <Spinner data-slot="icon" />}
            {resource ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** How many rows a page of the library shows. The endpoint sends all 252 in
 *  one payload, which is smaller than the directory's, so the paging is done
 *  here rather than adding a server-side page to a list this size. */
const PAGE_SIZE = 25;

export default function AdminResourcesPage() {
  const { data, isPending, error } = useAdminResources();
  const review = useReviewResource();
  const remove = useRemoveResource();
  const [editing, setEditing] = useState<AdminResource | "new" | null>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSortState] = useState<SortState>({ sort: null, direction: "asc" });
  // Any reorder puts different rows on page one, so stay on it.
  const setSort = (next: SortState) => {
    setSortState(next);
    setPage(1);
  };

  const pending = data?.resources.filter((r) => r.status === "pending") ?? [];
  const rest = data?.resources.filter((r) => r.status !== "pending") ?? [];
  const term = query.trim().toLowerCase();
  const found = term
    ? rest.filter((r) =>
        [r.title, r.category, r.url, ...r.tags]
          .join(" ")
          .toLowerCase()
          .includes(term)
      )
    : rest;
  // Client-side, unlike the member and legacy tables: those are windows onto
  // more rows than they hold, and this page already has every resource in
  // memory. Todd, after the 1 September demo: "admin resources: allow ordering
  // (they may also want by recently created)."
  const ordered = useMemo(() => {
    if (!sort.sort) return found;
    const pick = (r: AdminResource) => {
      switch (sort.sort) {
        case "category":
          return r.category.toLowerCase();
        case "access":
          return r.access;
        case "status":
          return r.status;
        case "added":
          return r.created_at ?? "";
        default:
          return r.title.toLowerCase();
      }
    };
    const rows = [...found].sort((a, b) =>
      String(pick(a)).localeCompare(String(pick(b)), undefined, { sensitivity: "base" })
    );
    return sort.direction === "desc" ? rows.reverse() : rows;
  }, [found, sort]);

  const pages = Math.max(1, Math.ceil(ordered.length / PAGE_SIZE));
  const shown = ordered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const failure = error || review.error || remove.error;

  return (
    <div className="max-w-5xl space-y-6 pb-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl">Resources</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            The member library, plus suggestions waiting for review.
          </p>
        </div>
        <Button onClick={() => setEditing("new")}>
          <Plus data-slot="icon" /> Add resource
        </Button>
      </div>

      {failure && <p className="text-destructive text-sm">{failure.message}</p>}

      {editing && (
        <ResourceDialog
          key={editing === "new" ? "new" : editing.id}
          resource={editing === "new" ? null : editing}
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}

      {/* The queue reads as the library does, because deciding whether an entry
          belongs there means seeing what it will look like when it arrives. */}
      {pending.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className={sectionTitleClass}>
              Member suggestions ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pending.map((resource) => (
              <SuggestionCard
                key={resource.id}
                resource={resource}
                busy={review.isPending}
                onEdit={() => setEditing(resource)}
                onDecide={(decision) =>
                  review.mutate({ id: resource.id, decision })
                }
              />
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className={sectionTitleClass}>All resources</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative w-full sm:w-72">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              aria-label="Search resources"
              placeholder="Search resources"
              className="pl-8"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {isPending ? (
            <RowListSkeleton withAvatar={false} />
          ) : found.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              {term ? `Nothing matches "${query}".` : "Nothing published yet."}
            </p>
          ) : (
            <Table className="w-full min-w-[57rem] table-fixed text-sm">
              <TableHeader>
                <TableRow>
                  <SortableHeader column="title" state={sort} onSort={setSort}>
                    Name
                  </SortableHeader>
                  <SortableHeader column="category" state={sort} onSort={setSort} className="w-48">
                    Category
                  </SortableHeader>
                  <SortableHeader column="access" state={sort} onSort={setSort} className="w-32">
                    Access
                  </SortableHeader>
                  <SortableHeader column="status" state={sort} onSort={setSort} className="w-28">
                    Status
                  </SortableHeader>
                  <SortableHeader column="added" state={sort} onSort={setSort} className="w-28">
                    Added
                  </SortableHeader>
                  <TableHead className="w-36 text-right">Edit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((resource) => (
                  <TableRow key={resource.id}>
                    <TableCell className="truncate" title={resource.title}>
                      {resource.url ? (
                        <a
                          href={resource.url}
                          target="_blank"
                          rel="noreferrer"
                          className="underline-offset-4 hover:underline"
                        >
                          {resource.title}
                        </a>
                      ) : (
                        resource.title
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground truncate">
                      {resource.category}
                    </TableCell>
                    <TableCell>
                      {resource.access === "paid" ? (
                        <span className="inline-flex items-center gap-1">
                          <Lock className="size-3" /> Full
                        </span>
                      ) : (
                        <span className="text-muted-foreground">All</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={STATUS_VARIANT[resource.status] ?? "outline"}
                        className="text-xs"
                      >
                        {resource.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap tabular-nums">
                      {resource.created_at
                        ? new Date(resource.created_at).toLocaleDateString()
                        : "\u2014"}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing(resource)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-2"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(resource.id)}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {found.length > 0 && (
            <Pager
              page={page}
              pages={pages}
              total={found.length}
              noun="resources"
              onPage={setPage}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * A suggestion, shown the way the library shows an entry.
 *
 * Its actions sit at the top right rather than under the description: the
 * descriptions are member-written and run to any length, so buttons below them
 * landed at a different height in every card and each decision had to be
 * aimed at.
 */
function SuggestionCard({
  resource,
  busy,
  onEdit,
  onDecide,
}: {
  resource: AdminResource;
  busy: boolean;
  onEdit: () => void;
  onDecide: (decision: "published" | "declined") => void;
}) {
  return (
    <Card className="animate-item-enter border-dashed">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base">
                {resource.url ? (
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 underline-offset-4 hover:underline"
                  >
                    {resource.title} <ExternalLink className="size-3.5" />
                  </a>
                ) : (
                  resource.title
                )}
              </CardTitle>
              {resource.category && (
                <Badge variant="secondary">{resource.category}</Badge>
              )}
              {resource.access === "paid" && (
                <Badge variant="outline" className="gap-1">
                  <Lock className="size-3" /> Full members
                </Badge>
              )}
            </div>
            {resource.description && (
              <CardDescription>{resource.description}</CardDescription>
            )}
            <p className="text-muted-foreground text-xs">
              Suggested by {resource.submitter_name ?? "somebody not recorded"}
            </p>
            {resource.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {resource.tags.map((t) => (
                  <Badge key={t} variant="outline" className="text-xs">
                    {t}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            <Button size="sm" disabled={busy} onClick={() => onDecide("published")}>
              Publish
            </Button>
            <Button size="sm" variant="outline" onClick={onEdit}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => onDecide("declined")}
            >
              Decline
            </Button>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
}
